import type { RunsPage, TaskType, UseCaseRun } from "@/types/content";

import { apiClient } from "../client";
import type {
  ExtractTalkingHeadFramesResponse,
  RunTaskTypeRequest,
  RunTaskTypeResponse,
} from "./types";

async function getTaskTypes(): Promise<TaskType[]> {
  const { data, error } = await apiClient.GET("/api/task-types");
  if (error) throw new Error("Failed to load task types");
  return (data as unknown as TaskType[]) ?? [];
}

async function runTaskType(
  id: string,
  req: RunTaskTypeRequest,
): Promise<RunTaskTypeResponse> {
  const { data, error } = await apiClient.POST("/api/task-types/{id}/run", {
    params: { path: { id } },
    body: { inputs: req.inputs, mode: req.mode ?? "async", dispatch_only: true },
  });
  if (error || !data) throw new Error("Generation failed");
  return data as unknown as RunTaskTypeResponse;
}

async function getRuns(take = 20, skip = 0): Promise<RunsPage> {
  const { data, error } = await apiClient.GET("/content/use-case-runs/jobs", {
    params: { query: { take, skip } },
  });
  if (error || !data) throw new Error("Failed to load content");
  return data;
}

async function getRun(runId: string): Promise<UseCaseRun | null> {
  const { data, response, error } = await apiClient.GET(
    "/content/use-case-runs/jobs/{runId}",
    { params: { path: { runId } } },
  );
  if (response.status === 404) return null;
  if (error || !data) throw new Error("Failed to load run");
  return data;
}

async function extractTalkingHeadFrames(
  video: File,
): Promise<ExtractTalkingHeadFramesResponse> {
  const { data, error } = await apiClient.POST("/content/talking-head/extract-frames", {
    body: { video: video as unknown as string },
    bodySerializer() {
      const formData = new FormData();
      formData.append("video", video);
      return formData;
    },
  });
  if (error || !data) throw new Error("Failed to extract frames from video");
  return data as unknown as ExtractTalkingHeadFramesResponse;
}

export const apiContent = {
  getTaskTypes,
  runTaskType,
  getRuns,
  getRun,
  extractTalkingHeadFrames,
};
